import type { V1ConsoleUser, V1RegisterResponse } from '../client'
import type { AuthResponse, SocialProvider } from '../utils/auth-outcome'

import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'

import { completeOAuth, startOAuth } from '../api/auth'
import { authServiceGetMe, authServiceLogin, authServiceLogout, authServiceRegister, authServiceResetPassword, authServiceSendVerificationCode, authServiceVerifyVerificationCode } from '../client'
import { useApi } from '../composables/useApi'
import i18n from '../locales'
import { authResponseKind } from '../utils/auth-outcome'
import { errorToMessage } from '../utils/error'
import { PostHogTracker } from '../utils/posthog-tracker'
import { useRegionStore } from './region'

// Define user type for better type safety
export interface User extends V1ConsoleUser {
  // Ordered candidates: the identity provider's picture (WorkOS/OAuth
  // `avatarUrl`), then Gravatar. The UI falls back to the name initial when
  // every candidate fails to load.
  avatarSources?: string[]
}

// Define login credentials interface
export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

// Define register credentials interface
export interface RegisterCredentials {
  email: string
  password: string
  username?: string
  nickname?: string
  turnstileToken: string // Required
  invitationToken?: string
}

// Define verification code type
export type VerificationCodeType = 'VERIFICATION_CODE_TYPE_REGISTER' | 'VERIFICATION_CODE_TYPE_RESET_PASSWORD'

// Define send verification code credentials interface
export interface SendVerificationCodeCredentials {
  type: VerificationCodeType
  email: string
  turnstileToken?: string
  invitationToken?: string
}

// Define verify verification code credentials interface
export interface VerifyVerificationCodeCredentials {
  type: VerificationCodeType
  email: string
  code: string
  invitationToken?: string
  registrationPassword?: string
}

export interface OAuthVerification {
  email: string
  provider: 'google' | 'github'
  state: string
  isNewUser?: boolean
}

function authErrorPayload(error: unknown) {
  const cause = error instanceof Error && error.cause ? error.cause : error
  return cause && typeof cause === 'object' && 'data' in cause
    ? (cause as { data?: unknown }).data ?? cause
    : cause
}

function authErrorStatus(error: unknown): number | undefined {
  const payload = authErrorPayload(error)
  return payload && typeof payload === 'object'
    ? Number((payload as { error?: { status?: number | string }, status?: number | string }).error?.status
      ?? (payload as { status?: number | string }).status)
    : undefined
}

export function isPasswordRecoveryProofRequired(error: unknown): boolean {
  return authErrorStatus(error) === 403
}

export const useUserStore = defineStore('user', () => {
  const regionStore = useRegionStore()

  // Auth state lives in an HttpOnly cookie set by the server. JavaScript
  // cannot inspect it, so the source of truth for "is the user signed in"
  // becomes the in-memory `user` object — populated by `me()` after the
  // browser successfully attaches the cookie, cleared on 401.
  const user = ref<User | undefined>(undefined)

  // Preserve the social attempt across the callback URL cleanup/remount, without
  // persisting credentials or challenge details in browser storage.
  let pendingOAuthVerification: OAuthVerification | undefined
  const beginOAuthVerification = (verification: OAuthVerification) => {
    pendingOAuthVerification = verification
  }
  const takeOAuthVerification = () => {
    const verification = pendingOAuthVerification
    pendingOAuthVerification = undefined
    return verification
  }

  // Tri-state guard: until bootstrap() completes its first probe we don't
  // know whether the cookie is valid, so callers (router guards) must wait
  // before deciding to redirect.
  const authChecked = ref(false)
  let authGeneration = 0
  let isAuthBoundaryPending = true

  const isAuthenticated = computed(() => !!user.value)

  const getAuthGeneration = () => authGeneration
  const isCurrentAuthGeneration = (generation: number) => generation === authGeneration
  const beginAuthTransition = () => ++authGeneration

  const resolveAuthBoundary = () => {
    authChecked.value = true
    if (!isAuthBoundaryPending)
      return
    isAuthBoundaryPending = false
    PostHogTracker.user.authBootstrapResolved()
  }

  const startAuthBoundary = () => {
    authChecked.value = false
    isAuthBoundaryPending = true
    PostHogTracker.user.authBootstrapStarted()
  }

  const clearAuthState = () => {
    user.value = undefined
    PostHogTracker.user.reset()
  }

  const clearCurrentRegionData = () => {
    beginAuthTransition()
    clearAuthState()
    resolveAuthBoundary()
  }

  const handleUnauthorized = (requestGeneration = authGeneration): boolean => {
    // A response that started before a login, logout, or region change no
    // longer describes the active cookie/session and must not mutate it.
    if (!isCurrentAuthGeneration(requestGeneration))
      return false

    // Missing credentials are the expected state during the first anonymous
    // /me probe. In that case the cross-site anonymous PostHog identity must
    // remain intact. Once a user is loaded, however, a 401 means the active
    // session expired and all subsequent requests should see signed-out state.
    if (!user.value)
      return false

    beginAuthTransition()
    PostHogTracker.auth.sessionExpired()
    clearAuthState()
    resolveAuthBoundary()
    // This is the only path that represents an involuntary session loss, so
    // the "please log in again" toast lives here rather than in the App.vue
    // isAuthenticated watcher — a deliberate logout also ends with `user`
    // going undefined and must not surface it. Concurrent 401s are deduped
    // by the generation bump above: later calls bail out before reaching this.
    toast.error(i18n.global.t('error.unauthorized'))
    return true
  }

  const login = async (credentials: LoginCredentials) => {
    const operationGeneration = beginAuthTransition()
    try {
      const { data } = (await useApi(authServiceLogin, {
        body: {
          email: credentials.email,
          password: credentials.password,
        },
      }, { silent: true, handleUnauthorized: false })).unwrap()

      // Server still echoes the JWT in `token` for now but we deliberately
      // ignore it: the auth cookie is the only credential the frontend uses.
      const responseData = (data ?? {}) as AuthResponse
      if (authResponseKind(responseData) !== 'authenticated')
        return responseData
      const userData = responseData.user

      if (isCurrentAuthGeneration(operationGeneration)) {
        user.value = userData as User
        PostHogTracker.user.identify(user.value)
      }

      return responseData
    }
    finally {
      // This attempt superseded the older /me probe, so once it settles it owns
      // and releases the analytics identity boundary (success or failure).
      if (isCurrentAuthGeneration(operationGeneration))
        resolveAuthBoundary()
    }
  }

  const register = async (credentials: RegisterCredentials): Promise<V1RegisterResponse> => {
    // Registration either creates an account or requests invitation ownership proof.
    const result = await useApi(authServiceRegister, {
      body: {
        email: credentials.email,
        password: credentials.password,
        username: credentials.username,
        nickname: credentials.nickname,
        turnstile_token: credentials.turnstileToken,
        invitationToken: credentials.invitationToken,
      },
    }, { silent: true })

    // The signup form shows the error inline, so surface the API error itself
    // (unwrap) rather than a generic message (expect). Empty data is success.
    const { data } = result.unwrap()

    // No need to set user or token, user needs to verify email and login separately
    return data ?? {}
  }

  const startOAuthLogin = async (provider: SocialProvider, redirectUri: string, returnTo: string, invitationToken?: string) => {
    const { data } = (await useApi(startOAuth, {
      body: { provider, redirectUri, returnTo, invitationToken },
    }, { silent: true, handleUnauthorized: false })).expect('OAuth login failed')
    return data
  }

  const completeOAuthLogin = async (code: string, state: string) => {
    const operationGeneration = beginAuthTransition()
    try {
      const { data } = (await useApi(completeOAuth, {
        body: { code, state },
      }, { silent: true, handleUnauthorized: false })).expect('OAuth login failed')
      const responseData = (data ?? {}) as AuthResponse
      if (authResponseKind(responseData) !== 'authenticated')
        return responseData
      if (isCurrentAuthGeneration(operationGeneration)) {
        user.value = responseData.user as User
        PostHogTracker.user.identify(user.value)
      }
      return responseData
    }
    finally {
      if (isCurrentAuthGeneration(operationGeneration))
        resolveAuthBoundary()
    }
  }

  const isFetchingMe = ref(false)
  const meRequests = new Map<number, Promise<void>>()
  const ME_REQUEST_TIMEOUT_MS = 30_000

  const fetchMe = async (requestGeneration: number) => {
    // Always fetch latest user data from API to ensure permissions are up to date
    // Don't rely on cached data as permissions may have changed
    try {
      const controller = new AbortController()
      let timer: ReturnType<typeof setTimeout> | undefined
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          const error = new DOMException('User request timed out', 'TimeoutError')
          controller.abort(error)
          reject(error)
        }, ME_REQUEST_TIMEOUT_MS)
      })
      const response = await Promise.race([
        useApi(authServiceGetMe, { signal: controller.signal }, { silent: true })
          .then(result => result.expect('Failed to load user')),
        timeout,
      ]).finally(() => {
        if (timer !== undefined)
          clearTimeout(timer)
      })
      const { data } = response
      const userData = data?.user
      if (!userData) {
        throw new Error('User data missing')
      }
      if (!isCurrentAuthGeneration(requestGeneration))
        return

      const userWithAvatar = { ...userData } as User

      const avatarSources: string[] = []
      if (userWithAvatar.avatarUrl)
        avatarSources.push(userWithAvatar.avatarUrl)
      if (userWithAvatar.email) {
        const emailHash = Array.from(
          new Uint8Array(
            await crypto.subtle.digest('SHA-256', new TextEncoder().encode(userWithAvatar.email.trim().toLowerCase())),
          ),
        ).map(b => b.toString(16).padStart(2, '0')).join('')
        // d=404 makes Gravatar fail for unregistered emails instead of serving
        // a generated identicon, so the initial fallback can take over.
        avatarSources.push(`https://www.gravatar.com/avatar/${emailHash}?d=404`)
      }
      userWithAvatar.avatarSources = avatarSources

      if (!isCurrentAuthGeneration(requestGeneration))
        return
      user.value = userWithAvatar
      PostHogTracker.user.identify(userWithAvatar)
    }
    catch (error) {
      // `Result.expect()` wraps the API failure in cause.data. Keep /me's
      // background request silent at the transport boundary, then surface one
      // non-auth error here; 401 already owns the session-expired UX.
      if (authErrorStatus(error) !== 401)
        toast.error(errorToMessage(authErrorPayload(error)))
      throw error
    }
  }

  const me = (requestGeneration = authGeneration): Promise<void> => {
    const existingRequest = meRequests.get(requestGeneration)
    if (existingRequest)
      return existingRequest

    isFetchingMe.value = true
    const request = fetchMe(requestGeneration).finally(() => {
      if (meRequests.get(requestGeneration) === request)
        meRequests.delete(requestGeneration)
      isFetchingMe.value = meRequests.size > 0
    })
    meRequests.set(requestGeneration, request)
    return request
  }

  const refreshSessionAfterVerification = async (): Promise<'authenticated' | 'unauthenticated' | 'superseded'> => {
    // The verification response can set a new cookie. Do not reuse a /me probe
    // that began before it, or let that older probe overwrite this identity.
    const operationGeneration = beginAuthTransition()
    try {
      await me(operationGeneration)
      return isCurrentAuthGeneration(operationGeneration) ? 'authenticated' : 'superseded'
    }
    catch (error) {
      if (!isCurrentAuthGeneration(operationGeneration))
        return 'superseded'
      if (authErrorStatus(error) === 401)
        return 'unauthenticated'
      throw error
    }
    finally {
      if (isCurrentAuthGeneration(operationGeneration))
        resolveAuthBoundary()
    }
  }

  // Probe the cookie against the API once at startup (and after region
  // switches) so we can populate `user` without a full page navigation.
  // 401s are swallowed: a missing/expired cookie just means "not signed in".
  //
  // DEV note: isAuthenticated short-circuits to true in dev so router guards
  // don't bounce you to /login, but the layout still gates rendering on a
  // populated `user`. We try me() anyway and swallow failures — if the cookie
  // is valid the UI works as expected; if not, the dev-bypass keeps things
  // navigable (user stays undefined but isAuthenticated remains true).
  const bootstrap = async () => {
    const bootstrapGeneration = authGeneration
    startAuthBoundary()
    try {
      await me(bootstrapGeneration)
    }
    catch {
      // Leave user undefined; isAuthenticated stays false (prod) or true
      // (dev) and guards route accordingly once authChecked flips true.
    }
    finally {
      // A newer login/logout/region bootstrap owns the auth boundary now.
      if (isCurrentAuthGeneration(bootstrapGeneration))
        resolveAuthBoundary()
    }
  }

  // The cookie is scoped to the shared root domain (e.g. .spatius.ai) and is
  // therefore the same across regions. On region switch we clear the cached
  // user and re-probe via bootstrap() against the new API host. authChecked
  // is reset first so guards don't kick the user to /login during the gap.
  watch(
    () => regionStore.currentRegionKey,
    () => {
      beginAuthTransition()
      authChecked.value = false
      clearAuthState()
      void bootstrap()
    },
  )

  const logout = async (): Promise<void> => {
    beginAuthTransition()
    PostHogTracker.auth.logoutStarted()
    // Clear the UI immediately, but retain the analytics identity until the
    // terminal logout event has been captured.
    user.value = undefined
    resolveAuthBoundary()

    // Ask the server to expire the auth cookie. We swallow errors because
    // local state is already cleared — a failed network call shouldn't
    // leave the UI showing a signed-in user.
    try {
      await useApi(authServiceLogout, { body: {} }, { silent: true, handleUnauthorized: false })
      PostHogTracker.auth.logoutSucceeded()
    }
    catch (error) {
      PostHogTracker.auth.logoutFailed(errorToMessage(error))
    }
    finally {
      PostHogTracker.user.reset()
    }
  }

  const sendVerificationCode = async (credentials: SendVerificationCodeCredentials) => {
    const { data } = (await useApi(authServiceSendVerificationCode, {
      body: {
        type: credentials.type,
        email: credentials.email,
        ...(credentials.turnstileToken ? { turnstileToken: credentials.turnstileToken } : {}),
        ...(credentials.invitationToken ? { invitationToken: credentials.invitationToken } : {}),
      },
    }, { handleUnauthorized: credentials.type === 'VERIFICATION_CODE_TYPE_REGISTER' })).expect('Failed to send verification code')

    return data
  }

  const verifyVerificationCode = async (credentials: VerifyVerificationCodeCredentials) => {
    if (credentials.type !== 'VERIFICATION_CODE_TYPE_REGISTER')
      throw new Error('Submit the recovery code together with the new password')
    const { data } = (await useApi(authServiceVerifyVerificationCode, {
      body: {
        type: credentials.type,
        email: credentials.email,
        code: credentials.code,
        ...(credentials.invitationToken ? { invitationToken: credentials.invitationToken } : {}),
        ...(credentials.registrationPassword ? { registrationPassword: credentials.registrationPassword } : {}),
      },
    })).expect('Failed to verify code')

    return data
  }

  const resetPassword = async (credentials: { email: string, code: string, newPassword: string }) => {
    const { data } = (await useApi(authServiceResetPassword, {
      body: { email: credentials.email, code: credentials.code, newPassword: credentials.newPassword },
    }, { handleUnauthorized: false })).expect('Failed to reset password')
    return data
  }

  return {
    isAuthenticated,
    authChecked,
    user,
    isFetchingMe,

    login,
    register,
    startOAuthLogin,
    completeOAuthLogin,
    beginOAuthVerification,
    takeOAuthVerification,
    refreshSessionAfterVerification,
    me,
    bootstrap,
    logout,
    sendVerificationCode,
    verifyVerificationCode,
    resetPassword,
    clearCurrentRegionData,
    getAuthGeneration,
    handleUnauthorized,
  }
})
